import { isBrowser } from 'browser-or-node';
import { whitelistedEnvVars } from 'shared/whitelisted-env-vars';
import { decodeComplianceConfig } from 'shared/core/helpers/compliance-serialization';

/**
 * @param {string} key
 * @param {string|undefined} value
 * @returns {string}
 */
function getRequired(key, value) {
  if (!value && process.env.NODE_ENV !== 'test') {
    // TODO: make an Error when test envs set these vars
    console.warn(`Required PublicEnv var ${key} is not set`);
  }
  return value;
}

// If we're in the browser, these values will come from /api/config.js and be attached to the window.
// Otherwise, we're doing server rendering, so we have direct access to the environment variables.
// Note: When setting up a new application you will need to have a script tag that loads these in,
// e.g <script src='/api/config.js' />
const values = (isBrowser ? window.reactEnv : whitelistedEnvVars) ?? {};

const PublicEnv = {
  // mandatory
  adminUrl: getRequired('ADMIN_URL', values.adminUrl),
  aerosyncConfigurationId: getRequired('AEROSYNC_CONFIGURATION_ID', values.aerosyncConfigurationId),
  amplitudeApiKey: getRequired('AMPLITUDE_API_KEY', values.amplitudeApiKey),
  apiUrl: getRequired('API_URL', values.apiUrl),
  appEnv: getRequired('APP_ENV', values.appEnv),
  basisTheoryKey: getRequired('BASIS_THEORY_KEY', values.basisTheoryKey),
  consumerConciergeMfRemoteUrl: getRequired('CONSUMER_CONCIERGE_MF_REMOTE_URL', values.consumerConciergeMfRemoteUrl),
  consumerUrl: getRequired('CONSUMER_URL', values.consumerUrl),
  googleAnalyticsID: 'UA-101536475-1',
  graphqlUri: `${getRequired('API_URL', values.apiUrl)}/graphql`,
  launchDarklyClientId: getRequired('LAUNCH_DARKLY_CLIENT_ID', values.launchDarklyClientId),
  paysafeEnvironment: getRequired('PAYSAFE_ENVIRONMENT', values.paysafeEnvironment),
  pendoApiKey: getRequired('PENDO_API_KEY', values.pendoApiKey),
  plaidEnv: getRequired('PLAID_ENV', values.plaidEnv),
  plaidKey: getRequired('PLAID_KEY', values.plaidKey),
  pusherKey: getRequired('PUSHER_KEY', values.pusherKey),
  siftBeaconKey: getRequired('SIFT_BEACON_KEY', values.siftBeaconKey),
  streamKey: getRequired('STREAM_MESSAGING_KEY_ID', values.streamKey),
  stripeKey: getRequired('STRIPE_PKEY', values.stripeKey),
  turnstileSiteKey: getRequired('CF_TURNSTILE_SITE_KEY', values.turnstileSiteKey),

  // optional
  // Remote compliance config delivered by the API via react-env; decode the Infinity sentinels.
  complianceConfig: values.complianceConfig ? decodeComplianceConfig(values.complianceConfig) : undefined,
  // Base URL of the Web Studio (CMS) Cloudflare Worker, used by the CMS editors' Puck AI plugin.
  // Optional: only the CMS editor routes need it, and it is not set in every environment.
  cmsApiUrl: values.cmsApiUrl,
  // Environment-specific public namespace for Web Studio preview hosts. Production's
  // dutchie.website remains the safe default when an environment does not override it.
  cmsPreviewDomainSuffix:
    values.cmsPreviewDomainSuffix?.trim().toLowerCase().replace(/^\.+|\.+$/g, '') || 'dutchie.website',
  isE2E: values.isE2E,
  currentVersion: values.currentVersion,
  // Explicit opt-in for the dev account picker (see DevAccountPicker). Off unless an
  // environment deliberately sets ENABLE_DEV_ACCOUNT_PICKER=true — never inferred from
  // appEnv, so it can't leak into demo/staging just because they aren't "production".
  enableDevAccountPicker: values.enableDevAccountPicker,
  gtmContainerKey: values.gtmContainerKey,
  launchDarklyBaseUri: values.launchDarklyBaseUri,
  launchDarklyStreamUri: values.launchDarklyStreamUri,
  launchDarklyEventsUri: values.launchDarklyEventsUri,
  sessionContextCookieName: values.sessionContextCookieName,
  vectorPersonalizeApiUrl: values.vectorPersonalizeApiUrl,
};

export default PublicEnv;
